import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircleIcon, 
  SparklesIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  TrophyIcon,
  StarIcon,
  HomeIcon
} from '@heroicons/react/24/solid';
import {
  selectProgress,
  selectSessionData,
  selectCompletionSummary,
} from '../store/slices/interviewSlice';

const InterviewComplete = () => {
  const progress = useSelector(selectProgress);
  const sessionData = useSelector(selectSessionData);
  const completionSummary = useSelector(selectCompletionSummary);
  const navigate = useNavigate();
  const [showConfetti, setShowConfetti] = useState(true);
  const [animateIn, setAnimateIn] = useState(false);

  // Trigger entrance animation
  useEffect(() => {
    setTimeout(() => setAnimateIn(true), 100);
    // Hide confetti after 5 seconds
    setTimeout(() => setShowConfetti(false), 5000);
  }, []);

  // Generate confetti particles
  const confettiColors = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899'];
  const confettiParticles = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 0.5,
    duration: 2 + Math.random() * 2,
    color: confettiColors[Math.floor(Math.random() * confettiColors.length)],
    rotation: Math.random() * 360,
  }));

  const questionsAnswered = progress?.answered_questions || 0;
  const totalQuestions = progress?.total_questions || 0;
  const percentComplete = progress?.completion_percentage || 100;

  /** @type {Record<string, any>} */
  const completionData = completionSummary || {};
  const actualDurationMinutes = completionData.actual_duration_minutes;
  const completedAt = completionData.completed_at || progress?.completed_at;
  const startedAt = completionData.started_at || progress?.started_at;
  const answeredCount = completionData.answered_questions ?? questionsAnswered;
  const totalCount = completionData.total_questions ?? totalQuestions;
  const skippedCount = completionData.skipped_questions ?? 0;
  const timedOutCount = completionData.timed_out_questions ?? 0;

  const getDurationLabel = () => {
    if (typeof actualDurationMinutes === 'number') {
      return `Completed in ${actualDurationMinutes} minute${actualDurationMinutes === 1 ? '' : 's'}`;
    }

    if (startedAt && completedAt) {
      const elapsedMinutes = Math.max(
        1,
        Math.round((new Date(completedAt).getTime() - new Date(startedAt).getTime()) / 60000)
      );
      return `Completed in ${elapsedMinutes} minute${elapsedMinutes === 1 ? '' : 's'}`;
    }

    return 'Interview completed';
  };

  const getCompletionTimeLabel = () => {
    if (!completedAt) {
      return 'Completion time unavailable';
    }

    return new Date(completedAt).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const achievements = [
    {
      icon: CheckCircleIcon,
      title: 'All Questions Answered',
      description: `${answeredCount} of ${totalCount || answeredCount} questions answered`,
      color: 'text-green-500',
      bg: 'bg-green-50'
    },
    {
      icon: ClockIcon,
      title: 'Time Invested',
      description: getDurationLabel(),
      color: 'text-blue-500',
      bg: 'bg-blue-50'
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: 'Responses Recorded',
      description: `${skippedCount} skipped, ${timedOutCount} timed out`,
      color: 'text-purple-500',
      bg: 'bg-purple-50'
    }
  ];

  const nextSteps = [
    {
      step: 1,
      title: 'Review Process',
      description: 'Our team will carefully review your responses',
      icon: '👀'
    },
    {
      step: 2,
      title: 'Evaluation',
      description: 'Your interview will be evaluated within 3-5 business days',
      icon: '📊'
    },
    {
      step: 3,
      title: 'Next Steps',
      description: "We'll contact you via email with the next steps",
      icon: '📧'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center px-4 py-8 relative overflow-hidden">
      
      {/* Confetti Animation */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50">
          {confettiParticles.map((particle) => (
            <div
              key={particle.id}
              className="absolute w-3 h-3 animate-confetti-fall"
              style={{
                left: `${particle.left}%`,
                top: '-20px',
                backgroundColor: particle.color,
                animationDelay: `${particle.delay}s`,
                animationDuration: `${particle.duration}s`,
                transform: `rotate(${particle.rotation}deg)`,
              }}
            />
          ))}
        </div>
      )}

      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-emerald-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-teal-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <div className={`relative max-w-4xl w-full transition-all duration-1000 ${
        animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}>
        
        {/* Main Success Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 sm:p-12 mb-6 border border-gray-100">
          
          {/* Trophy Icon */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full blur-xl opacity-50 animate-pulse"></div>
              <div className="relative w-24 h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                <TrophyIcon className="w-14 h-14 text-white" />
              </div>
            </div>
          </div>

          {/* Success Message */}
          <div className="text-center mb-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Interview Complete! 🎉
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Congratulations! You've successfully completed your interview for{' '}
              <span className="font-semibold text-gray-900">
                {sessionData?.job_position?.title || 'this position'}
              </span>
              . We appreciate the time and effort you've invested.
            </p>
          </div>

          {/* Completion Stats */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 mb-8 border border-green-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <StarIcon className="w-5 h-5 text-green-600" />
                <span className="text-sm font-semibold text-green-900">Performance Summary</span>
              </div>
              <div className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                {percentComplete.toFixed(0)}%
              </div>
            </div>
            
            <div className="h-3 bg-white rounded-full overflow-hidden shadow-inner">
              <div 
                className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-1000"
                style={{ width: `${percentComplete}%` }}
              >
                <div className="h-full bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer"></div>
              </div>
            </div>
            
            <div className="mt-3 text-center text-sm text-green-800 font-semibold">
              All questions answered
            </div>
          </div>

          {/* Completion Details */}
          <div className="bg-gray-50 rounded-2xl p-6 mb-8 border border-gray-200">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Completed at</p>
                <p className="mt-1 text-sm font-medium text-gray-900">{getCompletionTimeLabel()}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Duration</p>
                <p className="mt-1 text-sm font-medium text-gray-900">{getDurationLabel()}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Answered</p>
                <p className="mt-1 text-sm font-medium text-gray-900">{answeredCount} of {totalCount || answeredCount}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Skipped / Timed out</p>
                <p className="mt-1 text-sm font-medium text-gray-900">
                  {skippedCount} skipped, {timedOutCount} timed out
                </p>
              </div>
            </div>
          </div>

          {/* Achievement Cards */}
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            {achievements.map((achievement, index) => (
              <div 
                key={index}
                className={`${achievement.bg} rounded-xl p-4 transition-all duration-300 hover:shadow-lg hover:scale-105`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex flex-col items-center text-center">
                  <div className={`w-12 h-12 rounded-full bg-white flex items-center justify-center mb-3 shadow-sm`}>
                    <achievement.icon className={`w-6 h-6 ${achievement.color}`} />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1 text-sm">
                    {achievement.title}
                  </h3>
                  <p className="text-xs text-gray-600">
                    {achievement.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Appreciation Message */}
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-100">
            <div className="flex items-start space-x-3">
              <SparklesIcon className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  Thank You for Your Time
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Your thoughtful responses help us understand your skills and experience better. 
                  We know interviews require significant time and mental energy, and we truly 
                  appreciate your dedication throughout this process.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Next Steps Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 sm:p-12 border border-gray-100">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 text-center">
            What Happens Next?
          </h2>

          <div className="space-y-6">
            {nextSteps.map((step, index) => (
              <div 
                key={index}
                className="flex items-start space-x-4 p-4 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold shadow-lg">
                    {step.step}
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h3 className="font-semibold text-gray-900">
                      {step.title}
                    </h3>
                    <span className="text-xl">{step.icon}</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Contact Info */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-600 text-center">
              Questions? Contact us at{' '}
              <a href="mailto:support@teamsyncai.com" className="text-indigo-600 hover:text-indigo-700 font-medium underline">
                support@teamsyncai.com
              </a>
            </p>
          </div>

          {/* Close Window Button */}
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl shadow transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <HomeIcon className="w-5 h-5" />
              Go to Home
            </button>
            <p className="mt-3 text-xs text-gray-500">
              You can safely close this window now
            </p>
          </div>
        </div>

        {/* Footer Branding */}
        <div className="text-center mt-6 text-sm text-gray-500">
          Powered by <span className="font-semibold text-indigo-600">TeamSyncAI</span>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes confetti-fall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        
        .animate-confetti-fall {
          animation: confetti-fall linear;
          animation-fill-mode: forwards;
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
        
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(20px, -20px) scale(1.1); }
          50% { transform: translate(-20px, 20px) scale(0.9); }
          75% { transform: translate(20px, 20px) scale(1.05); }
        }
        
        .animate-blob {
          animation: blob 7s infinite;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default InterviewComplete;